import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { useBodyClass } from '@/hooks/useBodyClass';
import { buildDailyTargets } from '@/lib/tdee';
import type { Goal, ActivityLevel } from '@/types';

// ============================================
// ОБЩАЯ МОДАЛКА-ОБОЛОЧКА с sticky footer
// ============================================
function ModalWrapper({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  // Скрываем BottomNav пока модалка открыта
  useBodyClass('modal-open', open);
  
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-bg-card w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl flex flex-col"
            style={{ 
              maxHeight: '90dvh', // 🆕 dynamic viewport — учитывает клавиатуру
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — фиксирован */}
            <div className="bg-bg-card border-b border-line/40 px-5 py-4 flex items-center justify-between shrink-0">
              <h2 className="font-display font-bold text-lg">{title}</h2>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated flex items-center justify-center text-ink-muted hover:text-ink"
              >
                <X size={16} />
              </button>
            </div>
            
            {/* Контент — прокручиваемая зона */}
            <div className="flex-1 overflow-y-auto p-5">{children}</div>
            
            {/* 🎯 STICKY FOOTER — кнопки всегда видны */}
            {footer && (
              <div className="border-t border-line/40 px-5 py-3 bg-bg-card shrink-0">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============================================
// 1. МОДАЛКА — ПАРАМЕТРЫ ТЕЛА
// ============================================
export function EditParamsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [saving, setSaving] = useState(false);
  const [height, setHeight] = useState(profile?.height_cm?.toString() ?? '');
  const [targetWeight, setTargetWeight] = useState(profile?.target_weight_kg?.toString() ?? '');
  const [birthDate, setBirthDate] = useState(profile?.birth_date ?? '');
  
  if (!profile) return null;

  async function save() {
    const h = Number(height);
    const tw = targetWeight ? Number(targetWeight) : null;

    if (!h || h < 100 || h > 250) {
      alert('Введи корректный рост (100-250 см)');
      return;
    }
    if (tw !== null && (tw < 30 || tw > 200)) {
      alert('Введи корректный целевой вес (30-200 кг)');
      return;
    }

    setSaving(true);
    try {
      const updates: any = {
        height_cm: h,
        target_weight_kg: tw,
      };

      if (birthDate && birthDate !== profile?.birth_date) {
        updates.birth_date = birthDate;
      }

      if (
        profile.sex && 
        (birthDate || profile.birth_date) && 
        profile.weight_kg && 
        profile.goal && 
        profile.activity_level
      ) {
        const targets = buildDailyTargets({
          sex: profile.sex,
          birth_date: birthDate || profile.birth_date!,
          height_cm: h,
          weight_kg: profile.weight_kg,
          goal: profile.goal,
          activity_level: profile.activity_level,
        });
        Object.assign(updates, targets);
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile!.id)
        .select()
        .single();
      
      if (error) throw error;
      setProfile(data);
      haptic('success');
      onClose();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  const footer = (
    <div className="flex gap-2">
      <button
        onClick={onClose}
        className="flex-1 py-3 rounded-xl bg-bg-elevated text-ink-muted text-sm"
      >
        Отмена
      </button>
      <button
        onClick={save}
        disabled={saving}
        className="flex-1 py-3 rounded-xl bg-emerald-glow text-bg font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98]"
      >
        <Check size={14} />
        {saving ? 'Сохраняю...' : 'Сохранить'}
      </button>
    </div>
  );

  return (
    <ModalWrapper open={open} onClose={onClose} title="Параметры тела" footer={footer}>
      <p className="text-xs text-ink-muted mb-4 leading-relaxed">
        При изменении параметров автоматически пересчитается твоя дневная норма калорий.
      </p>

      <label className="label mb-2 block">Рост, см</label>
      <input
        type="number"
        inputMode="numeric"
        className="input mb-4"
        placeholder="175"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
      />

      <label className="label mb-2 block">Целевой вес, кг</label>
      <input
        type="number"
        inputMode="decimal"
        step="0.1"
        className="input mb-4"
        placeholder="70"
        value={targetWeight}
        onChange={(e) => setTargetWeight(e.target.value)}
      />

      <label className="label mb-2 block">
        Дата рождения 
        <span className="text-ink-dim text-[10px] ml-1">(можно изменить)</span>
      </label>
      <input
        type="date"
        className="input"
        value={birthDate}
        onChange={(e) => setBirthDate(e.target.value)}
        max={new Date().toISOString().split('T')[0]}
      />
    </ModalWrapper>
  );
}

// ============================================
// 2. МОДАЛКА — ЦЕЛЬ
// ============================================
const GOALS: { value: Goal; emoji: string; label: string; description: string; calories: string }[] = [
  {
    value: 'lose',
    emoji: '📉',
    label: 'Похудеть',
    description: 'Постепенное снижение веса без жёстких диет',
    calories: 'Дефицит ~400 ккал/день',
  },
  {
    value: 'maintain',
    emoji: '⚖️',
    label: 'Поддержать форму',
    description: 'Удержать текущий вес, формировать привычки',
    calories: 'Норма калорий = расход',
  },
  {
    value: 'gain',
    emoji: '📈',
    label: 'Набрать массу',
    description: 'Рост мышц и общего веса',
    calories: 'Профицит ~400 ккал/день',
  },
];

export function EditGoalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Goal>(profile?.goal ?? 'maintain');

  if (!profile) return null;

  async function save() {
    setSaving(true);
    try {
      const updates: any = { goal: selected };

      if (
        profile.sex && 
        profile.birth_date && 
        profile.height_cm && 
        profile.weight_kg && 
        profile.activity_level
      ) {
        const targets = buildDailyTargets({
          sex: profile.sex,
          birth_date: profile.birth_date,
          height_cm: profile.height_cm,
          weight_kg: profile.weight_kg,
          goal: selected,
          activity_level: profile.activity_level,
        });
        Object.assign(updates, targets);
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile!.id)
        .select()
        .single();
      
      if (error) throw error;
      setProfile(data);
      haptic('success');
      onClose();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  const footer = (
    <div className="flex gap-2">
      <button
        onClick={onClose}
        className="flex-1 py-3 rounded-xl bg-bg-elevated text-ink-muted text-sm"
      >
        Отмена
      </button>
      <button
        onClick={save}
        disabled={saving}
        className="flex-1 py-3 rounded-xl bg-emerald-glow text-bg font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98]"
      >
        <Check size={14} />
        {saving ? 'Сохраняю...' : 'Сохранить'}
      </button>
    </div>
  );

  return (
    <ModalWrapper open={open} onClose={onClose} title="Твоя цель" footer={footer}>
      <p className="text-xs text-ink-muted mb-4 leading-relaxed">
        Выбери цель — приложение автоматически рассчитает норму калорий под неё.
      </p>

      <div className="space-y-2">
        {GOALS.map((g) => (
          <button
            key={g.value}
            onClick={() => setSelected(g.value)}
            className={`w-full p-4 rounded-2xl text-left transition-all border-2 ${
              selected === g.value
                ? 'border-emerald-glow bg-emerald-glow/5'
                : 'border-line bg-bg-card'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="text-3xl shrink-0">{g.emoji}</div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-display font-bold">{g.label}</div>
                  {selected === g.value && (
                    <Check size={16} className="text-emerald-glow" />
                  )}
                </div>
                <div className="text-xs text-ink-muted mb-1.5 leading-relaxed">
                  {g.description}
                </div>
                <div className="text-[11px] text-emerald-glow">
                  {g.calories}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </ModalWrapper>
  );
}

// ============================================
// 3. МОДАЛКА — УРОВЕНЬ АКТИВНОСТИ
// ============================================
const ACTIVITY_LEVELS: {
  value: ActivityLevel;
  emoji: string;
  label: string;
  description: string;
  steps: string;
  examples: string;
}[] = [
  {
    value: 'sedentary',
    emoji: '🪑',
    label: 'Малоподвижный',
    description: 'Работа сидя за компом, дома лежу на диване. Никаких тренировок.',
    steps: '3 000 – 5 000 шагов в день',
    examples: 'Программист, бухгалтер, водитель',
  },
  {
    value: 'light',
    emoji: '🚶',
    label: 'Лёгкая активность',
    description: 'Гуляю каждый день 30-60 мин или иногда тренируюсь 1-2 раза в неделю.',
    steps: '7 000 – 10 000 шагов',
    examples: 'Офисный работник который ходит обедать, мама с коляской, лёгкие домашние дела',
  },
  {
    value: 'moderate',
    emoji: '🏃',
    label: 'Умеренная',
    description: 'Тренируюсь регулярно 3-4 раза в неделю или работа на ногах.',
    steps: '10 000+ шагов',
    examples: 'Продавец, тренер раз через день, медсестра, преподаватель',
  },
  {
    value: 'active',
    emoji: '💪',
    label: 'Активный',
    description: 'Тренируюсь 5-6 раз в неделю или физически тяжёлая работа.',
    steps: '12 000+ шагов',
    examples: 'Строитель, грузчик, спортсмен-любитель, курьер',
  },
  {
    value: 'very_active',
    emoji: '🔥',
    label: 'Очень активный',
    description: 'Профессиональный спорт или экстремально физическая работа. Тренировки каждый день.',
    steps: '15 000+ шагов',
    examples: 'Профессиональный спортсмен, шахтёр, лесоруб',
  },
];

export function EditActivityModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<ActivityLevel>(profile?.activity_level ?? 'moderate');

  if (!profile) return null;

  async function save() {
    setSaving(true);
    try {
      const updates: any = { activity_level: selected };

      if (
        profile.sex && 
        profile.birth_date && 
        profile.height_cm && 
        profile.weight_kg && 
        profile.goal
      ) {
        const targets = buildDailyTargets({
          sex: profile.sex,
          birth_date: profile.birth_date,
          height_cm: profile.height_cm,
          weight_kg: profile.weight_kg,
          goal: profile.goal,
          activity_level: selected,
        });
        Object.assign(updates, targets);
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile!.id)
        .select()
        .single();
      
      if (error) throw error;
      setProfile(data);
      haptic('success');
      onClose();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  const footer = (
    <div className="flex gap-2">
      <button
        onClick={onClose}
        className="flex-1 py-3 rounded-xl bg-bg-elevated text-ink-muted text-sm"
      >
        Отмена
      </button>
      <button
        onClick={save}
        disabled={saving}
        className="flex-1 py-3 rounded-xl bg-emerald-glow text-bg font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98]"
      >
        <Check size={14} />
        {saving ? 'Сохраняю...' : 'Сохранить'}
      </button>
    </div>
  );

  return (
    <ModalWrapper open={open} onClose={onClose} title="Уровень активности" footer={footer}>
      <p className="text-xs text-ink-muted mb-4 leading-relaxed">
        Выбери что лучше всего описывает твой обычный день. Учитывается всё: работа, тренировки, ходьба.
      </p>

      <div className="space-y-2 mb-4">
        {ACTIVITY_LEVELS.map((l) => (
          <button
            key={l.value}
            onClick={() => setSelected(l.value)}
            className={`w-full p-4 rounded-2xl text-left transition-all border-2 ${
              selected === l.value
                ? 'border-emerald-glow bg-emerald-glow/5'
                : 'border-line bg-bg-card'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="text-3xl shrink-0">{l.emoji}</div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-display font-bold text-sm">{l.label}</div>
                  {selected === l.value && (
                    <Check size={16} className="text-emerald-glow" />
                  )}
                </div>
                <div className="text-xs text-ink-muted mb-2 leading-relaxed">
                  {l.description}
                </div>
                <div className="text-[11px] text-sky-glow mb-1">
                  👟 {l.steps}
                </div>
                <div className="text-[10px] text-ink-dim leading-relaxed">
                  Примеры: {l.examples}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-warn/5 border border-warn/20 rounded-lg p-3">
        <p className="text-[11px] text-ink-muted leading-relaxed">
          💡 Уровень активности влияет на расчёт твоей дневной нормы калорий. 
          Если ты выберешь слишком высокий или низкий — нормы могут быть неточными.
        </p>
      </div>
    </ModalWrapper>
  );
}
